import { useEffect, useId, useState, type ReactNode } from 'react'
import { SPANS, UTC_CLOCK, clockWithSeconds, type Clock, type SpanKey } from '@/lib/schedule/timeline'
import { formatOffset } from '@/lib/time'

/** Every narrowing the board applies, in one object so the page owns one state. */
export interface GanttFilters {
  group: string
  category: string
  type: string
  station: string
  country: string
  /** Substring of the flight number. Free text, not a code. */
  flight: string
  showCancelled: boolean
}

export type Grouping = 'regn' | 'aircraft'

/** One choice in a filter: the value it sets, and the text it is found by. */
export interface FilterOption {
  value: string
  text: string
}

export interface FilterOptions {
  groups: FilterOption[]
  categories: FilterOption[]
  types: FilterOption[]
  stations: FilterOption[]
  countries: FilterOption[]
  flights: FilterOption[]
}

/**
 * Frame, clock and filters.
 *
 * The clock control switches labels only. The frame is an interval of UTC
 * instants either way, so a board read in local time and the same board read
 * in UTC are the same board — see timeline.ts.
 *
 * Every filter is a typed field with suggestions rather than a drop-down: 76
 * stations and a thousand flight numbers are a scroll, and three letters are
 * not.
 */
export function GanttToolbar({
  span,
  onSpan,
  clock,
  onClockMode,
  baseStation,
  onBaseStation,
  localZones,
  dateValue,
  dateMin,
  dateMax,
  onDate,
  onStep,
  onNow,
  grouping,
  onGrouping,
  filters,
  onFilters,
  options,
  children,
}: {
  span: SpanKey
  onSpan: (span: SpanKey) => void
  clock: Clock
  onClockMode: (mode: 'utc' | 'local') => void
  baseStation: string
  onBaseStation: (station: string) => void
  localZones: { code: string; offsetSeconds: number }[]
  dateValue: string
  /** Days the loaded period covers. Empty when nothing is loaded. */
  dateMin: string
  dateMax: string
  onDate: (date: string) => void
  onStep: (direction: 1 | -1) => void
  onNow: () => void
  grouping: Grouping
  onGrouping: (grouping: Grouping) => void
  filters: GanttFilters
  onFilters: (filters: GanttFilters) => void
  options: FilterOptions
  /** Import control and counts, placed at the end of the second row. */
  children?: ReactNode
}) {
  const set = <K extends keyof GanttFilters>(key: K, value: GanttFilters[K]) =>
    onFilters({ ...filters, [key]: value })

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex items-center gap-1">
          <StepButton label="Previous frame" onClick={() => onStep(-1)}>
            <path d="M11 4 6 9l5 5" />
          </StepButton>
          <input
            type="date"
            aria-label="Date"
            value={dateValue}
            min={dateMin || undefined}
            max={dateMax || undefined}
            onChange={(event) => onDate(event.target.value)}
            className="t-number-sm h-9 rounded-[8px] border border-hairline bg-canvas px-2 text-ink
                       outline-none focus:border-primary"
          />
          <StepButton label="Next frame" onClick={() => onStep(1)}>
            <path d="M7 4l5 5-5 5" />
          </StepButton>
          <button
            type="button"
            onClick={onNow}
            className="t-caption-strong ml-1 h-9 rounded-[100px] bg-surface-strong px-3 text-ink
                       transition-colors active:bg-hairline"
          >
            Now
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-1 rounded-[100px] bg-surface-soft p-1">
          {SPANS.map((entry) => (
            <button
              key={entry.key}
              type="button"
              aria-pressed={span === entry.key}
              onClick={() => onSpan(entry.key)}
              className={`t-caption-strong h-7 rounded-[100px] px-2.5 transition-colors ${
                span === entry.key ? 'bg-canvas text-ink shadow-soft' : 'text-muted hover:text-ink'
              }`}
            >
              {entry.key}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <LiveClock clock={clock} baseStation={baseStation} />

          <div className="flex items-center gap-1 rounded-[100px] bg-surface-soft p-1">
            {(['utc', 'local'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                aria-pressed={clock.mode === mode}
                onClick={() => onClockMode(mode)}
                className={`t-caption-strong h-7 rounded-[100px] px-3 transition-colors ${
                  clock.mode === mode ? 'bg-canvas text-ink shadow-soft' : 'text-muted hover:text-ink'
                }`}
              >
                {mode === 'utc' ? 'UTC' : 'Local'}
              </button>
            ))}
          </div>

          {clock.mode === 'local' && (
            <select
              aria-label="Local station"
              value={baseStation}
              onChange={(event) => onBaseStation(event.target.value)}
              className="t-number-sm h-9 rounded-[8px] border border-hairline bg-canvas px-2 text-ink
                         outline-none focus:border-primary"
            >
              {localZones.map((zone) => (
                <option key={zone.code} value={zone.code}>
                  {zone.code} {formatOffset(zone.offsetSeconds)}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
        <Combo
          label="Group"
          width={96}
          value={filters.group}
          options={options.groups}
          onChange={(value) => set('group', value)}
        />
        <Combo
          label="Cat"
          width={88}
          value={filters.category}
          options={options.categories}
          onChange={(value) => set('category', value)}
        />
        <Combo
          label="Type"
          width={120}
          value={filters.type}
          options={options.types}
          onChange={(value) => set('type', value)}
        />

        <span className="mx-0.5 h-6 w-px bg-hairline" aria-hidden="true" />

        <Combo
          label="Airport"
          width={132}
          value={filters.station}
          options={options.stations}
          onChange={(value) => set('station', value)}
        />
        <Combo
          label="Country"
          width={132}
          value={filters.country}
          options={options.countries}
          onChange={(value) => set('country', value)}
        />
        <Combo
          label="Flight"
          width={96}
          value={filters.flight}
          options={options.flights}
          onChange={(value) => set('flight', value)}
        />

        <span className="mx-0.5 h-6 w-px bg-hairline" aria-hidden="true" />

        <div className="flex items-center gap-1 rounded-[100px] bg-surface-soft p-1">
          {(['aircraft', 'regn'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              aria-pressed={grouping === mode}
              onClick={() => onGrouping(mode)}
              className={`t-caption-strong h-7 rounded-[100px] px-3 transition-colors ${
                grouping === mode ? 'bg-canvas text-ink shadow-soft' : 'text-muted hover:text-ink'
              }`}
            >
              {mode === 'regn' ? 'REGN' : 'Aircraft'}
            </button>
          ))}
        </div>

        <label className="t-caption-strong flex h-9 cursor-pointer items-center gap-2 px-1 text-body">
          <input
            type="checkbox"
            checked={filters.showCancelled}
            onChange={(event) => set('showCancelled', event.target.checked)}
            className="h-4 w-4 accent-[color:var(--color-primary)]"
          />
          Cancelled
        </label>

        <div className="ml-auto flex items-center gap-3">{children}</div>
      </div>
    </div>
  )
}

/**
 * Wall clock, second by second.
 *
 * Both clocks at once on purpose: a shift reads the board in station time and
 * files everything in UTC, and the pair of them side by side is the one place
 * the offset is never in doubt.
 */
function LiveClock({ clock, baseStation }: { clock: Clock; baseStation: string }) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    // Aligned to the next whole second so the digits change when the world's
    // do, not 400ms later.
    let timer = 0
    const tick = () => {
      setNow(Date.now())
      timer = window.setTimeout(tick, 1000 - (Date.now() % 1000))
    }
    timer = window.setTimeout(tick, 1000 - (Date.now() % 1000))
    return () => window.clearTimeout(timer)
  }, [])

  return (
    <p className="t-number-sm whitespace-nowrap text-ink">
      {clock.mode === 'local' && (
        <>
          <span className="text-muted">{baseStation}</span> {clockWithSeconds(now, clock)}
          <span className="text-muted"> · </span>
        </>
      )}
      <span className="text-muted">UTC</span> {clockWithSeconds(now, UTC_CLOCK)}
    </p>
  )
}

/**
 * A field with suggestions.
 *
 * `datalist` rather than a hand-built dropdown: it is the platform's own
 * type-ahead, it filters on what the user types without a keystroke handler,
 * and it stays usable with a keyboard and a screen reader for free. Each
 * option's value carries both the code and the name — "ICN — Incheon" — so a
 * station is found by either, and the page resolves the text back to a code.
 */
function Combo({
  label,
  value,
  options,
  width,
  onChange,
}: {
  label: string
  value: string
  options: FilterOption[]
  width: number
  onChange: (value: string) => void
}) {
  const id = useId()
  return (
    <div className="relative" style={{ width }}>
      <input
        list={id}
        value={value}
        aria-label={label}
        placeholder={label}
        onChange={(event) => onChange(event.target.value)}
        className={`t-body-sm h-9 w-full rounded-[8px] border bg-canvas px-2 pr-6 text-ink
                    outline-none placeholder:text-body focus:border-primary ${
                      value ? 'border-primary' : 'border-hairline'
                    }`}
      />
      <datalist id={id}>
        {options.map((option) => (
          <option key={option.value} value={option.text} />
        ))}
      </datalist>
      {value && (
        <button
          type="button"
          aria-label={`Clear ${label}`}
          tabIndex={-1}
          onClick={() => onChange('')}
          className="absolute top-1/2 right-1 -translate-y-1/2 rounded-[6px] p-1 text-muted
                     transition-colors hover:bg-surface-strong"
        >
          <svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  )
}

function StepButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="grid h-9 w-9 place-items-center rounded-[8px] border border-hairline text-body
                 transition-colors hover:bg-surface-soft active:bg-surface-strong"
    >
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
        <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          {children}
        </g>
      </svg>
    </button>
  )
}
